// CANCEL-003 — client-side builder for the cancellation request email.
//
// Produces the exact body the engineer previews in the "See the email"
// disclosure. Keep in sync with the backend's text builder at
// backend/notifications/strategies/order.py::_build_text_body — any change
// to wording here must change there too, or the engineer's preview and the
// branch's email will diverge. Ported from the mock design source
// (octopus-mock/lib/order-cancellation.ts :: buildCancelEmail, lines 85-126).
import type { CancellationReason } from '../auth/services/auth-api'

export interface CancelEmailInput {
  // Required order fields.
  orderNumber: string
  supplierName: string
  branchName: string
  // Optional / nullable order fields.
  supplierOrderNumber: string | null
  accountNumber: string
  jobNumber: string
  itemCount: number
  totalAmount: number
  // 'collection' or 'delivery to {address}'.
  fulfillmentMode: 'pickup' | 'delivery'
  deliveryAddress: string | null
  // Engineer identity for signature + CC + Reply-To.
  engineerName: string
  engineerEmail: string
  // Branch email — the request target.
  branchEmail: string
  // The engineer's selection and optional note. Reason may be null before
  // the engineer picks one; the preview shows "(not chosen yet)" then.
  reason: CancellationReason | null
  reasonLabel: string | null
  note: string
}

export interface CancelEmail {
  to: string
  cc: string
  subject: string
  body: string
}

function formatTotal(amount: number): string {
  return `£${amount.toFixed(2)}`
}

export function buildCancelEmail(input: CancelEmailInput): CancelEmail {
  const supplierOrder = input.supplierOrderNumber || 'not issued yet'
  const itemWord = input.itemCount === 1 ? 'item' : 'items'
  const fulfillment =
    input.fulfillmentMode === 'pickup'
      ? 'collection'
      : `delivery to ${input.deliveryAddress || 'the job address'}`
  const trimmedNote = input.note.trim()

  const lines: string[] = [
    `Hello ${input.supplierName} ${input.branchName} team,`,
    '',
    'Please could you cancel this order if you still can.',
    '',
    `Your order no.: ${supplierOrder}`,
    `Our order no.: ${input.orderNumber}`,
  ]
  if (input.accountNumber) {
    lines.push(`Account no.: ${input.accountNumber}`)
  }
  if (input.jobNumber) {
    lines.push(`Job no.: ${input.jobNumber}`)
  }
  lines.push(
    `${input.itemCount} ${itemWord}, ${formatTotal(input.totalAmount)}, ${fulfillment}`
  )
  lines.push('', `Reason: ${input.reasonLabel ?? '(not chosen yet)'}`)
  if (trimmedNote) {
    lines.push(`Note: ${trimmedNote}`)
  }
  lines.push(
    '',
    'This is a request, not a confirmed cancellation. Please reply to let me know whether you can cancel it, or if it has already gone out.',
    '',
    'Thanks,',
    `${input.engineerName}, Octopus Energy`,
    'Sent from SupplyScanner'
  )

  const subjectReference = input.supplierOrderNumber || input.orderNumber

  return {
    to: input.branchEmail,
    cc: input.engineerEmail,
    subject: `Cancellation request: order ${subjectReference}`,
    body: lines.join('\n'),
  }
}
